import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

// Execuções dos carregadores de banco (tabela carga_execucoes, alimentada pelos
// serviços de sincronização no Cloud Run). Substituiu o e-mail de "banco
// atualizado" que chegava a cada execução: agora só erro vira e-mail.

const API = process.env.REACT_APP_API_BASE;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access')}` });

const STATUS = {
  success: { rotulo: 'Concluída', cor: '#1fa971', fundo: '#e7f6ec', icone: 'fa-circle-check' },
  parcial: { rotulo: 'Parcial', cor: '#b45309', fundo: '#fdf0e0', icone: 'fa-triangle-exclamation' },
  error: { rotulo: 'Erro', cor: '#b42318', fundo: '#fdecea', icone: 'fa-circle-xmark' },
};

const ORIGENS = {
  conta_azul_sync: 'Conta Azul — sincronização',
  conta_azul_integral: 'Conta Azul — carga integral',
  omie: 'Omie',
};

const fmtDataHora = (s) => {
  if (!s) return '—';
  const d = new Date(String(s).replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return String(s);
  return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const fmtDuracao = (seg) => {
  const n = Number(seg || 0);
  if (n < 60) return `${n}s`;
  return `${Math.floor(n / 60)}m ${n % 60}s`;
};

const fmtNumero = (n) => Number(n || 0).toLocaleString('pt-BR');

// "há quanto tempo" — é o que responde de relance "o banco está atualizado?"
const desdeQuando = (s) => {
  if (!s) return '';
  const d = new Date(String(s).replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return '';
  const min = Math.floor((Date.now() - d.getTime()) / 60000);
  if (min < 1) return 'agora';
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `há ${h}h`;
  const dias = Math.floor(h / 24);
  return `há ${dias} dia${dias > 1 ? 's' : ''}`;
};

const Selo = ({ status }) => {
  const e = STATUS[status] || { rotulo: status || '—', cor: '#667085', fundo: '#f1f3f5', icone: 'fa-circle' };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap',
      backgroundColor: e.fundo, color: e.cor, fontWeight: 600, fontSize: '0.8rem',
      padding: '4px 10px', borderRadius: 999,
    }}>
      <i className={`fas ${e.icone}`} style={{ fontSize: 11 }}></i>{e.rotulo}
    </span>
  );
};

export default function CargasBanco() {
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [dias, setDias] = useState(7);
  const [status, setStatus] = useState('');
  const [idCliente, setIdCliente] = useState('');
  const [origem, setOrigem] = useState('');
  const [busca, setBusca] = useState('');
  const [page, setPage] = useState(1);
  const [detalhe, setDetalhe] = useState(null);
  const [carregandoDetalhe, setCarregandoDetalhe] = useState(false);

  const pageSize = 50;

  const buscar = useCallback(async () => {
    setCarregando(true);
    try {
      const params = { dias, page, page_size: pageSize };
      if (status) params.status = status;
      if (idCliente) params.id_cliente = idCliente;
      if (origem) params.origem = origem;
      if (busca.trim()) params.busca = busca.trim();
      const r = await axios.get(`${API}/api/cargas-banco/`, { params, headers: headers() });
      if (r.data?.success) setDados(r.data);
      else toast.error(r.data?.erro || 'Falha ao carregar execuções');
    } catch (e) {
      toast.error(e.response?.data?.erro || 'Erro ao carregar execuções');
    } finally {
      setCarregando(false);
    }
  }, [dias, page, status, idCliente, origem, busca]);

  useEffect(() => { buscar(); }, [buscar]);

  // filtro mudou: volta para a primeira página
  useEffect(() => { setPage(1); }, [dias, status, idCliente, origem]);

  const abrirDetalhe = async (id) => {
    setCarregandoDetalhe(true);
    setDetalhe({ carregando: true });
    try {
      const r = await axios.get(`${API}/api/cargas-banco/`, { params: { id }, headers: headers() });
      if (r.data?.success) setDetalhe(r.data.execucao);
      else { toast.error(r.data?.erro || 'Falha ao abrir execução'); setDetalhe(null); }
    } catch (e) {
      toast.error('Erro ao abrir execução');
      setDetalhe(null);
    } finally {
      setCarregandoDetalhe(false);
    }
  };

  useEffect(() => {
    const esc = (e) => { if (e.key === 'Escape') setDetalhe(null); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, []);

  const execucoes = dados?.execucoes || [];
  const resumo = dados?.resumo || {};
  const ultimas = dados?.ultimas || [];
  const totalPaginas = Math.max(1, Math.ceil((dados?.total || 0) / pageSize));

  return (
    <div className="das-page fmp-brand-page">
      <div className="container-fluid mt-4 mb-5">

        {/* Cabeçalho */}
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div className="d-flex align-items-center">
                <i className="fas fa-database header-icon me-3" style={{ fontSize: 26 }}></i>
                <div>
                  <h2 className="titulo-pagina m-0">Cargas de Banco</h2>
                  <small className="text-muted">
                    Cada execução dos sincronizadores fica registrada aqui. E-mail só é enviado quando há erro.
                  </small>
                </div>
              </div>
              <button className="btn btn-outline-primary" onClick={buscar} disabled={carregando}>
                <i className={`fas fa-rotate me-2 ${carregando ? 'fa-spin' : ''}`}></i>Atualizar
              </button>
            </div>
          </div>
        </div>

        {dados?.aviso && (
          <div className="alert alert-light border mb-4">
            <i className="fas fa-circle-info me-2" style={{ color: '#00205b' }}></i>{dados.aviso}
          </div>
        )}

        {/* Situação por cliente: a resposta rápida para "o banco está atualizado?" */}
        {ultimas.length > 0 && (
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-body">
              <h6 className="text-muted mb-3" style={{ fontWeight: 600 }}>
                <i className="fas fa-clock-rotate-left me-2"></i>Última carga de cada cliente
              </h6>
              <div className="row g-3">
                {ultimas.map((u) => (
                  <div key={u.id_cliente} className="col-md-4 col-lg-3">
                    <div className="border rounded p-3 h-100">
                      <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                        <strong style={{ fontSize: '0.95rem' }}>{u.nome_cliente || `Cliente ${u.id_cliente}`}</strong>
                        <Selo status={u.status} />
                      </div>
                      <div className="text-muted" style={{ fontSize: '0.82rem' }}>
                        {fmtDataHora(u.data_hora)} <span className="ms-1">({desdeQuando(u.data_hora)})</span>
                      </div>
                      <div className="text-muted" style={{ fontSize: '0.82rem' }}>
                        {fmtNumero(u.registros)} registros · {fmtDuracao(u.duracao_seg)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Filtros */}
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-body">
            <div className="row g-3 align-items-end">
              <div className="col-md-2">
                <label className="form-label small fw-semibold mb-1">Período</label>
                <select className="form-select" value={dias} onChange={(e) => setDias(Number(e.target.value))}>
                  <option value={1}>Último dia</option>
                  <option value={7}>7 dias</option>
                  <option value={30}>30 dias</option>
                  <option value={90}>90 dias</option>
                </select>
              </div>
              <div className="col-md-2">
                <label className="form-label small fw-semibold mb-1">Situação</label>
                <select className="form-select" value={status} onChange={(e) => setStatus(e.target.value)}>
                  <option value="">Todas</option>
                  <option value="success">Concluída</option>
                  <option value="parcial">Parcial</option>
                  <option value="error">Erro</option>
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label small fw-semibold mb-1">Cliente</label>
                <select className="form-select" value={idCliente} onChange={(e) => setIdCliente(e.target.value)}>
                  <option value="">Todos</option>
                  {(dados?.clientes || []).map((c) => (
                    <option key={c.id} value={c.id}>{c.nome || `Cliente ${c.id}`}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-2">
                <label className="form-label small fw-semibold mb-1">Origem</label>
                <select className="form-select" value={origem} onChange={(e) => setOrigem(e.target.value)}>
                  <option value="">Todas</option>
                  {(dados?.origens || []).map((o) => (
                    <option key={o} value={o}>{ORIGENS[o] || o}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label small fw-semibold mb-1">Buscar</label>
                <div className="input-group">
                  <input
                    className="form-control" placeholder="cliente, banco ou mensagem"
                    value={busca} onChange={(e) => setBusca(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { setPage(1); buscar(); } }}
                  />
                  <button className="btn btn-outline-secondary" onClick={() => { setPage(1); buscar(); }}>
                    <i className="fas fa-magnifying-glass"></i>
                  </button>
                </div>
              </div>
            </div>

            {/* Resumo do período */}
            <div className="d-flex gap-3 flex-wrap mt-3">
              {['success', 'parcial', 'error'].map((k) => (
                <button
                  key={k} type="button"
                  className="btn btn-sm"
                  style={{
                    border: `1px solid ${status === k ? STATUS[k].cor : '#e9ecef'}`,
                    backgroundColor: status === k ? STATUS[k].fundo : '#fff',
                    color: STATUS[k].cor, fontWeight: 600,
                  }}
                  onClick={() => setStatus(status === k ? '' : k)}
                >
                  <i className={`fas ${STATUS[k].icone} me-2`}></i>
                  {STATUS[k].rotulo}: {resumo[k] || 0}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Tabela */}
        <div className="card border-0 shadow-sm">
          <div className="card-body p-0">
            {carregando && !dados ? (
              <div className="text-center py-5">
                <div className="spinner-border" style={{ color: '#00205b' }}></div>
                <p className="mt-3 text-muted">Carregando execuções...</p>
              </div>
            ) : execucoes.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <i className="fas fa-inbox fa-3x mb-3" style={{ color: '#cbd5e1' }}></i>
                <p className="mb-0">Nenhuma execução no período selecionado.</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: '14%' }}>Data / hora</th>
                      <th>Cliente</th>
                      <th style={{ width: '16%' }}>Origem</th>
                      <th className="text-center" style={{ width: '10%' }}>Situação</th>
                      <th className="text-end" style={{ width: '10%' }}>Registros</th>
                      <th className="text-end" style={{ width: '8%' }}>Duração</th>
                      <th style={{ width: '20%' }}>Mensagem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {execucoes.map((e) => (
                      <tr key={e.id} style={{ cursor: 'pointer' }} onClick={() => abrirDetalhe(e.id)}
                          title="Ver o resultado de cada etapa">
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {fmtDataHora(e.data_hora)}
                          <div className="text-muted" style={{ fontSize: '0.75rem' }}>{desdeQuando(e.data_hora)}</div>
                        </td>
                        <td>
                          <strong>{e.nome_cliente || `Cliente ${e.id_cliente || '—'}`}</strong>
                          {e.banco && <div className="text-muted" style={{ fontSize: '0.78rem' }}>{e.banco}</div>}
                        </td>
                        <td className="text-muted" style={{ fontSize: '0.85rem' }}>{ORIGENS[e.origem] || e.origem}</td>
                        <td className="text-center"><Selo status={e.status} /></td>
                        <td className="text-end" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtNumero(e.registros)}</td>
                        <td className="text-end text-muted" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtDuracao(e.duracao_seg)}</td>
                        <td className="text-muted" style={{ fontSize: '0.82rem' }}>{e.mensagem || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Paginação */}
        {totalPaginas > 1 && (
          <div className="d-flex justify-content-between align-items-center mt-3">
            <small className="text-muted">
              {dados.total} execução(ões) · página {page} de {totalPaginas}
            </small>
            <div className="d-flex gap-2">
              <button className="btn btn-outline-secondary btn-sm" disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}>
                <i className="fas fa-chevron-left me-1"></i>Anterior
              </button>
              <button className="btn btn-outline-secondary btn-sm" disabled={page >= totalPaginas}
                onClick={() => setPage((p) => p + 1)}>
                Próxima<i className="fas fa-chevron-right ms-1"></i>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Detalhe */}
      {detalhe && (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,.5)' }}
             onClick={() => setDetalhe(null)}>
          <div className="modal-dialog modal-lg modal-dialog-centered" onClick={(ev) => ev.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <div>
                  <h5 className="modal-title" style={{ color: '#212529' }}>
                    <i className="fas fa-list-check me-2"></i>Resultado por etapa
                  </h5>
                  {!detalhe.carregando && (
                    <small style={{ color: '#6c757d' }}>
                      {detalhe.nome_cliente || `Cliente ${detalhe.id_cliente}`} · {fmtDataHora(detalhe.data_hora)}
                    </small>
                  )}
                </div>
                <button type="button" className="btn-close" onClick={() => setDetalhe(null)}></button>
              </div>
              <div className="modal-body">
                {carregandoDetalhe || detalhe.carregando ? (
                  <div className="text-center py-4">
                    <div className="spinner-border spinner-border-sm" style={{ color: '#00205b' }}></div>
                  </div>
                ) : (
                  <>
                    <div className="row g-3 mb-3">
                      <div className="col-md-3">
                        <div className="text-muted small">Situação</div>
                        <Selo status={detalhe.status} />
                      </div>
                      <div className="col-md-3">
                        <div className="text-muted small">Duração</div>
                        <strong>{fmtDuracao(detalhe.duracao_seg)}</strong>
                      </div>
                      <div className="col-md-3">
                        <div className="text-muted small">Registros</div>
                        <strong>{fmtNumero(detalhe.registros)}</strong>
                      </div>
                      <div className="col-md-3">
                        <div className="text-muted small">Banco</div>
                        <strong>{detalhe.banco || '—'}</strong>
                      </div>
                    </div>

                    {detalhe.mensagem && (
                      <div className="alert alert-warning py-2" style={{ fontSize: '0.88rem' }}>
                        <i className="fas fa-triangle-exclamation me-2"></i>{detalhe.mensagem}
                      </div>
                    )}

                    <table className="table table-sm align-middle">
                      <thead className="table-light">
                        <tr><th>Etapa</th><th>Resultado</th></tr>
                      </thead>
                      <tbody>
                        {Object.entries(detalhe.detalhes || {}).map(([etapa, valor]) => {
                          const ok = String(valor).toUpperCase().startsWith('OK');
                          return (
                            <tr key={etapa}>
                              <td style={{ textTransform: 'capitalize' }}>{etapa.replace(/_/g, ' ')}</td>
                              <td style={{ color: ok ? '#1fa971' : '#b42318' }}>
                                <i className={`fas ${ok ? 'fa-circle-check' : 'fa-circle-xmark'} me-2`}></i>
                                {String(valor)}
                              </td>
                            </tr>
                          );
                        })}
                        {Object.keys(detalhe.detalhes || {}).length === 0 && (
                          <tr><td colSpan={2} className="text-center text-muted">Sem detalhe por etapa.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setDetalhe(null)}>
                  <i className="fas fa-times me-1"></i>Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
